import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useBranding, assetUrl } from "@/lib/branding";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Factory, ArrowRight, Eye, EyeOff } from "lucide-react";
import { LoginSlideshow } from "@/components/LoginSlideshow";
import { isPortalHost, portalLoginUrl } from "@/lib/hostRouting";

const FALLBACK_IMAGE = "https://images.unsplash.com/photo-1509413031665-0d52b03f5cde?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Login() {
  const { user, login } = useAuth();
  const { branding } = useBranding();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);

  if (isPortalHost()) return <Navigate to="/portal/login" replace />;
  if (user) return <Navigate to="/" replace />;

  const companyName = branding?.company_name || "Hi-Tech Silico";
  const tagline = branding?.tagline || "Silicone Extrusions";
  const welcome = branding?.welcome_message || "Welcome back — manage leads, production and dispatch in one place.";
  const loginLogo = assetUrl(branding?.login_logo_url);
  const slides = (branding?.login_slides || []).map((s) => ({ ...s, url: assetUrl(s.url) }));
  const panelSlides = slides.length ? slides : [{ url: assetUrl(branding?.login_image_url) || FALLBACK_IMAGE }];

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password, remember);
      toast.success(`Welcome back, ${u?.name || ""}`.trim());
      sessionStorage.setItem("hts_intro", "1");
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white">
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 sm:px-16 py-12">
        <div className="max-w-md w-full mx-auto fade-up">
          <div className="flex items-center gap-3 mb-10" data-testid="login-brand">
            {loginLogo ? (
              <img src={loginLogo} alt={companyName} className="max-h-24 max-w-[360px] object-contain" />
            ) : (
              <>
                <div className="w-11 h-11 rounded-lg bg-[#3B82F6] text-white flex items-center justify-center">
                  <Factory size={20} strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-display font-extrabold tracking-tight text-slate-900 text-lg leading-none uppercase">{companyName}</div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#FC5F5F] mt-1.5">{tagline}</div>
                </div>
              </>
            )}
          </div>

          <div className="inline-flex rounded-full border border-slate-200 bg-slate-100 p-1 mb-8" data-testid="login-mode-toggle">
            <span className="px-4 py-1.5 text-xs font-semibold rounded-full bg-[#3B82F6] text-white">Staff CRM</span>
            <button type="button" onClick={() => window.location.assign(portalLoginUrl())} data-testid="switch-to-portal"
                    className="px-4 py-1.5 text-xs font-semibold rounded-full text-slate-500 hover:text-slate-800 transition-colors duration-200">
              Client Portal
            </button>
          </div>

          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FC5F5F] mb-3">Manufacturing CRM</div>
          <h1 className="text-3xl sm:text-4xl font-display font-light text-slate-900 tracking-tight">Sign in to your workspace</h1>
          <p className="text-sm text-slate-500 mt-3 leading-relaxed" data-testid="login-welcome">{welcome}</p>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-slate-500">Email</Label>
              <Input
                id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email" required autoComplete="email"
                className="mt-2 h-11 bg-slate-50 border-slate-200 focus:bg-white"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-slate-500">Password</Label>
              <div className="relative mt-2">
                <Input
                  id="password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password" required autoComplete="current-password"
                  className="h-11 bg-slate-50 border-slate-200 focus:bg-white pr-10"
                />
                <button type="button" onClick={() => setShowPw((v) => !v)} tabIndex={-1}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors duration-200"
                        title={showPw ? "Hide password" : "Show password"} data-testid="login-password-toggle">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
                <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="login-remember" />
                Remember me
              </label>
              <Link to="/forgot-password" className="text-xs font-medium text-[#FC5F5F] hover:underline" data-testid="login-forgot-link">
                Forgot password?
              </Link>
            </div>
            <Button
              type="submit" disabled={busy} data-testid="login-submit"
              className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-full h-11 text-sm font-semibold tracking-wide group"
            >
              {busy ? "Signing in…" : <>Sign in <ArrowRight size={15} className="ml-1.5 transition-transform duration-200 group-hover:translate-x-0.5" /></>}
            </Button>
          </form>
        </div>
      </div>

      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden bg-[#3B82F6]" data-testid="login-image-panel">
        <LoginSlideshow slides={panelSlides} fit={branding?.login_image_fit || "cover"} />
      </div>
    </div>
  );
}
